import { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import axiosInstance from "../../../../hooks/axiosInstance";

export default function EditProduct({ showEdit, closeModal, refreshPage, product }) {
  const [validationError, setValidationError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    product_name: "",
    category_id: "",
    sub_category_id: "",
    status: "",
    price: "",
    unit_of_measure_id: "",
    group_id: "",
    simple_code_id: "",
    description1: "",
    description2: ""
  });

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [unitsOfMeasure, setUnitsOfMeasure] = useState([]);
  const [groups, setGroups] = useState([]);
  const [simpleCodes, setSimpleCodes] = useState([]); // State for simple codes

  // Fetch categories, units of measure, and groups
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get('/user/category/fetch');
        setCategories(response.data.data.category.data);

        const unitsResponse = await axiosInstance.get('/user/product/units');
        setUnitsOfMeasure(unitsResponse.data.data.unitsOfMeasure);

        const groupsResponse = await axiosInstance.get('/user/group/groups-no-paginate');
        setGroups(groupsResponse.data.data.groups);
      } catch (error) {
        console.error("Error fetching data", error);
      }
    };

    fetchData();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (formData.category_id) {
        try {
          const response = await axiosInstance.get(`/user/category/getSubCategory?category_id=${formData.category_id}`);
          setSubCategories(response.data.data.subCategories);
        } catch (error) {
          console.error("Error fetching subcategories", error);
        }
      }
    };

    fetchSubCategories();
  }, [formData.category_id]);

  // Fetch simple codes when group changes
  useEffect(() => {
    const fetchSimpleCodes = async () => {
      if (formData.group_id) {
        try {
          const response = await axiosInstance.get(`/user/simple_code/fetch?group_id=${formData.group_id}`);
          const fetchedSimpleCodes = response.data.data.simpleCodes
          setSimpleCodes(fetchedSimpleCodes);
          // If no simple codes are found, clear any previous selection
          if (fetchedSimpleCodes.length === 0) {
            setFormData((prevFormData) => ({
              ...prevFormData,
              simple_code_id: "",
            }));
          }
        } catch (error) {
          console.error("Error fetching simple codes", error);
          setSimpleCodes([]); // Clear if an error occurs
          setFormData((prevFormData) => ({
            ...prevFormData,
            simple_code_id: "",
          }));
        }
      } else {
        setSimpleCodes([]); // Reset if no group is selected
        setFormData((prevFormData) => ({
          ...prevFormData,
          simple_code_id: "",
        }));
      }
    }

    fetchSimpleCodes();
  }, [formData.group_id]);

  useEffect(() => {
    if (product) {
      setFormData({
        product_name: product.product_name,
        category_id: product.category_id,
        sub_category_id: product.sub_category_id,
        status: product.status,
        price: product.price,
        unit_of_measure_id: product.unit_of_measure_id,
        group_id: product.group_id,
        simple_code_id: product.simple_code_id,
        description1: product.description1,
        description2: product.description2
      });
    }
  }, [product]);

  const resetForm = () => {
    setFormData({
      product_name: "",
      category_id: "",
      sub_category_id: "",
      status: "",
      price: "",
      unit_of_measure_id: "",
      group_id: "",
      simple_code_id: "",
      description1: "",
      description2: ""
    });
    setValidationError({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleClose = () => {
    resetForm();
    closeModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axiosInstance.post(`/user/product/edit/${product.id}`, formData);
      refreshPage();
      handleClose();
    } catch (error) {
      console.error("Error updating product", error);
    }
    setIsLoading(false);
  };

  return (
    <div className="EditProduct">
      <Modal show={showEdit} onHide={handleClose} dialogClassName="modal-md">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Product Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="formBasicName">
              <div>
                <h6>Product Name</h6>
              </div>
              <Form.Control
                type="text"
                placeholder="Enter..."
                name="product_name"
                value={formData.product_name}
                onChange={handleChange}
              />
              {validationError.product_name && (
                <small className="text-danger my-1">{validationError.product_name[0]}</small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formCategory">
              <div>
                <h6>Category</h6>
              </div>
              <Form.Select
                aria-label="Select Category"
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
              {validationError.category_id && (
                <small className="text-danger my-1">{validationError.category_id[0]}</small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formSubCategory">
              <div>
                <h6>Sub-category</h6>
              </div>
              <Form.Select
                aria-label="Select Sub-category"
                name="sub_category_id"
                value={formData.sub_category_id}
                onChange={handleChange}
              >
                <option value="">Select Sub-category</option>
                {subCategories.map((subCat) => (
                  <option key={subCat.id} value={subCat.id}>
                    {subCat.name}
                  </option>
                ))}
              </Form.Select>
              {validationError.sub_category_id && (
                <small className="text-danger my-1">{validationError.sub_category_id[0]}</small>
              )}
            </Form.Group>
            <Form.Group className="mb-3" controlId="formGroup">
              <div>
                <h6>Group</h6>
              </div>
              <Form.Select
                aria-label="Select Group"
                name="group_id"
                value={formData.group_id}
                onChange={handleChange}
              >
                <option value="">Select Group</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Form.Select>
              {validationError.group_id && (
                <small className="text-danger my-1">{validationError.group_id[0]}</small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formSimpleCode">
              <div>
                <h6>Simple Code</h6>
              </div>
              <Form.Select
                aria-label="Select Simple Code"
                name="simple_code_id"
                value={formData.simple_code_id}
                onChange={handleChange}
              >
                <option value="">Select Simple Code</option>
                {simpleCodes.map((code) => (
                  <option key={code.id} value={code.id}>
                    {code.simple_code}
                  </option>
                ))}
              </Form.Select>
              {validationError.simple_code_id && (
                <small className="text-danger my-1">{validationError.simple_code_id[0]}</small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formPrice">
              <div>
                <h6>Price</h6>
              </div>
              <Form.Control
                type="text"
                placeholder="Enter..."
                name="price"
                value={formData.price}
                onChange={handleChange}
              />
              {validationError.price && (
                <small className="text-danger my-1">{validationError.price[0]}</small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formUnitOfMeasure">
              <div>
                <h6>Unit of Measure</h6>
              </div>
              <Form.Select
                aria-label="Select Unit of Measure"
                name="unit_of_measure_id"
                value={formData.unit_of_measure_id}
                onChange={handleChange}
              >
                <option value="">Select Unit of Measure</option>
                {unitsOfMeasure.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Form.Select>
              {validationError.unit_of_measure_id && (
                <small className="text-danger my-1">{validationError.unit_of_measure_id[0]}</small>
              )}
            </Form.Group>

           
            <Form.Group className="mb-3" controlId="formDescription1">
              <div>
                <h6>Description 1</h6>
              </div>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter..."
                name="description1"
                value={formData.description1}
                onChange={handleChange}
              />
              {validationError.description1 && (
                <small className="text-danger my-1">{validationError.description1[0]}</small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formDescription2">
              <div>
                <h6>Description 2</h6>
              </div>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter..."
                name="description2"
                value={formData.description2}
                onChange={handleChange}
              />
              {validationError.description2 && (
                <small className="text-danger my-1">{validationError.description2[0]}</small>
              )}
            </Form.Group>
            <Form.Group className="mb-3" controlId="formUnitOfMeasure">
              <div>
                <h6>Status</h6>
              </div>
              <Form.Select
                aria-label="Select Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="">-- Select Status --</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
              {validationError.status && (
                <small className="text-danger my-1">
                  {validationError.status[0]}
                </small>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button variant="primary" type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
